import React, { useEffect, useState, useTransition } from 'react';
import {
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	FormLabel,
	MenuItem,
	SelectChangeEvent,
	Stack,
	Tooltip,
} from '@mui/material';
import { useAppSelector } from '../../../../store/hooks';
import { callAPIAsync, callAPISync, getOrganisationUsers, getOrganizationRoles, postAssignOrgRole } from '../../../../utils/api';
import { UserDetails } from '../../../../types/user';
import { GetOrganisationRolesResponse, GetOrganisationUsersResponse } from '../../../../types/api';
import LabelledSelect from '../../../LabelledSelect';
import { SnackbarAlert } from '../../../SnackbarAlert';
import { useLogto } from '@logto/react';

const ManageOrgRoles: React.FC = () => {
	const logto = useLogto();

	const userOrgs = useAppSelector((state) => state.user.orgs);
	const orgsToShow = userOrgs.filter((org) => org.permissions.includes('assign:role'));

	const [users, setUsers] = useState<Record<string, UserDetails>>({});
	const [orgRoles, setOrgRoles] = useState<GetOrganisationRolesResponse[]>([]);
	const [selectedUser, setSelectedUser] = useState('');
	const [selectedOrgRoles, setSelectedOrgRoles] = useState<string[]>([]);
	const [initialOrgRoles, setInitialOrgRoles] = useState<string[]>([]);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState('');
	const [isLoadingSubmit, setIsLoading] = useState(false);
	const [selectedOrg, setSelectedOrg] = useState('');
	const [loadingOrgRoles, startTransition] = useTransition();

	const isLoading = isLoadingSubmit || loadingOrgRoles;

	const changesMade = (): boolean => {
		if (selectedUser && selectedOrg) {
			const tempUser = Object.entries(users).filter(([id]) => id === selectedUser)[0][1];
			const allFound = tempUser.orgRoles?.every((role) => selectedOrgRoles.includes(role.id));
			const noneMissing = selectedOrgRoles.every((role) => tempUser.orgRoles?.some((orgRole) => orgRole.id === role));
			return !allFound || !noneMissing;
		}
		return false;
	};

	const getUsers = (organisation: string) => {
		callAPISync(logto, (token) => {
			const responseErrors: string[] = [];
			const receivedUsers: Record<string, UserDetails> = {};

			// should only find users in selected organisation
			const organisationObj = orgsToShow.filter((org) => org.id === organisation)[0];

			getOrganisationUsers(token, organisationObj.id)
				.then((orgResp) => {
					if (orgResp.ok) {
						return orgResp.json() as Promise<GetOrganisationUsersResponse[]>;
					} else {
						return [];
					}
				})
				.then((userResp) => {
					userResp.forEach((resp) => {
						if (resp.id in receivedUsers) {
							receivedUsers[resp.id].orgs.push(organisationObj);
						} else {
							receivedUsers[resp.id] = {
								name: resp.name,
								email: resp.primaryEmail,
								orgs: [organisationObj],
								roles: resp.roles,
								orgRoles: resp.organizationRoles,
							};
						}
					});

					if (responseErrors.length > 0 && Object.keys(receivedUsers).length === 0) {
						setError('Failed to fetch users in organisation.');
					}

					setUsers(
						Object.fromEntries(
							Object.entries(receivedUsers).sort(([, userA], [, userB]) => {
								return userA.name.localeCompare(userB.name);
							})
						)
					);

					setIsLoading(false);
				});
		});
	};

	const handleOrgInit = () => {
		setUsers({});
		if (orgsToShow.length === 1) {
			setSelectedOrg(orgsToShow[0].id);
			getUsers(orgsToShow[0].id);
		} else {
			setSelectedOrg('');
		}
	};

	useEffect(() => {
		startTransition(async () => {
			handleOrgInit();
			await callAPIAsync(logto, async (token) => {
				getOrganizationRoles(token)
					.then((orgRoleResp) => {
						if (orgRoleResp.ok) {
							return orgRoleResp.json() as Promise<GetOrganisationRolesResponse[]>;
						} else {
							return [];
						}
					})
					.then((orgRoles) => setOrgRoles(orgRoles));
			});
		});
	}, []);

	const handleSelectRole = (role: GetOrganisationRolesResponse, checked: boolean) => {
		if (checked) {
			setSelectedOrgRoles([...selectedOrgRoles, role.id]);
		} else {
			setSelectedOrgRoles(selectedOrgRoles.filter((orgRole) => orgRole !== role.id));
		}
	};

	const handleSelectOrg = async (event: SelectChangeEvent) => {
		setSelectedOrg(event.target.value);
		getUsers(event.target.value);
	};

	const handleSelectUser = async (event: SelectChangeEvent) => {
		setSelectedUser(event.target.value);
		const tempUser = Object.entries(users).filter(([id]) => id === event.target.value)[0][1];
		const tempOrgRoles = tempUser.orgRoles?.map((orgRole) => orgRole.id) as string[];
		setInitialOrgRoles(tempOrgRoles);
		setSelectedOrgRoles(tempOrgRoles);
	};

	const onSubmit = async () => {
		setIsLoading(true);
		if (selectedOrg && selectedUser && changesMade()) {
			callAPISync(logto, (token) => {
				postAssignOrgRole(token, selectedOrg, selectedUser, selectedOrgRoles, initialOrgRoles).then((res) => {
					if (res.status === 200) {
						setSuccess(true);
					} else {
						setError(`Failed to save roles: ${res.statusText}`);
					}
					handleOrgInit();
					setSelectedUser('');
					setSelectedOrgRoles([]);
					setInitialOrgRoles([]);
				});
			});
		}
	};

	return (
		<Stack
			sx={{
				width: '100%',
			}}
			spacing={2}
			data-testid="operations-tab-content"
			flex={1}
		>
			<Tooltip title={orgsToShow.length === 1 ? 'Not enabled as you only belong to one organisation' : 'Select an organisation to manage'}>
				<FormControl fullWidth variant="outlined">
					<LabelledSelect
						selectLabel="Organisation"
						testId="organisation-selector"
						value={selectedOrg}
						onChange={handleSelectOrg}
						disabled={orgsToShow.length < 2}
						MenuProps={{ disableScrollLock: true }}
						fullWidth
					>
						{orgsToShow.map((org) => (
							<MenuItem key={org.id} value={org.id} data-testid="org-option">
								{org.name}
							</MenuItem>
						))}
					</LabelledSelect>

					<FormHelperText>{`The organisation to manage`}</FormHelperText>
				</FormControl>
			</Tooltip>

			<FormControl fullWidth variant="outlined">
				<LabelledSelect
					selectLabel={isLoading ? 'Loading users...' : 'User'}
					testId="user-selector"
					value={selectedUser}
					onChange={handleSelectUser}
					fullWidth
					MenuProps={{ disableScrollLock: true }}
					disabled={!selectedOrg || isLoading}
				>
					{Object.entries(users).map(([id, user]) => (
						<MenuItem key={id} value={id} data-testid={`${user.name}-user-option`}>
							{user.name}
						</MenuItem>
					))}
				</LabelledSelect>
				<FormHelperText>{`The user to assign/unassign roles from`}</FormHelperText>
			</FormControl>

			<FormControl component="fieldset" variant="standard" disabled={!selectedUser} sx={{ paddingLeft: '5px' }}>
				<FormLabel sx={{ '&.Mui-focused': { color: '#ffffff' } }}>Assign organisation roles</FormLabel>
				<FormGroup sx={{ maxWidth: '50%' }}>
					{selectedUser &&
						orgRoles
							.filter((role) => !role.scopes.some((scope) => scope.name === 'assign:role'))
							.map((role) => (
								<Tooltip title={role.description} key={role.id}>
									<FormControlLabel
										control={
											<Checkbox
												data-testid={`${role.name}-role-checkbox`}
												checked={selectedOrgRoles.includes(role.id)}
												onChange={(_, checked) => handleSelectRole(role, checked)}
												name={role.name}
											/>
										}
										label={role.name}
									/>
								</Tooltip>
							))}
				</FormGroup>
				<FormHelperText>{`You cannot assign another user to the 'Manager' role.`}</FormHelperText>
			</FormControl>

			<Button
				variant="contained"
				fullWidth
				onClick={onSubmit}
				disabled={isLoading || !changesMade() || !selectedUser}
				data-testid="manage-role-submit-button"
				sx={{ '&.Mui-disabled': { bgcolor: '#60808A' } }}
			>
				{isLoading ? 'In Progress' : 'Submit'}
			</Button>

			<SnackbarAlert severity="success" open={success} onClose={() => setSuccess(false)} alertMessage="Successfully saved new roles" />

			<SnackbarAlert severity="error" open={error !== ''} onClose={() => setError('')} alertMessage={error} />
		</Stack>
	);
};

export default ManageOrgRoles;
